import React, { useState, useEffect } from 'react';
import { fetchProfitMarginReport } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;
const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProfitMargin({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [status, setStatus] = useState(0);
  const [shipType, setShipType] = useState('');
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchProfitMarginReport({
      StartDate: toApiDate(start),
      EndDate: toApiDate(end),
      Status: status,
      ShipType: shipType || undefined,
      SpecialOrders: 0,
      Category: 0,
      PageNumber: page,
      PageSize: PAGE_SIZE,
    })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const rawRows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const rows = Array.isArray(rawRows) ? rawRows : [];
  const total = data?.totalRows ?? data?.total ?? rows.length;
  const summary = !Array.isArray(data) ? (data?.summary ?? data?.totals ?? null) : null;

  const totalRev = rows.reduce((s, r) => s + Number(r.orderTotal ?? r.revenue ?? 0), 0);
  const totalProfit = rows.reduce((s, r) => s + Number(r.profit ?? 0), 0);
  const avgMargin = totalRev > 0 ? (totalProfit / totalRev * 100) : 0;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Profit Margin Report</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>STATUS</div>
            <select value={status} onChange={e => setStatus(Number(e.target.value))} className="form-select" style={{ fontSize: 11 }}>
              <option value={0}>All</option>
              <option value={1}>Shipped</option>
              <option value={2}>Pending</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>SHIP TYPE</div>
            <input type="text" value={shipType} onChange={e => setShipType(e.target.value)} placeholder="All types" className="form-input" style={{ fontSize: 11, width: 110 }} />
          </div>
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
        </div>
      </div>

      {(rows.length > 0 || summary) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Total Orders', value: (total || rows.length).toLocaleString() },
            { label: 'Total Revenue', value: summary ? fmt(summary.totalRevenue ?? summary.totalOrderAmount) : fmt(totalRev) },
            { label: 'Total Profit', value: summary ? fmt(summary.totalProfit) : fmt(totalProfit), highlight: true },
            { label: 'Avg Margin', value: summary ? `${Number(summary.avgMargin ?? summary.margin ?? avgMargin).toFixed(1)}%` : `${avgMargin.toFixed(1)}%`, highlight: true },
          ].map(({ label, value, highlight }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: highlight ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Order Detail {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data found.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Type</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Revenue</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Profit</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Margin</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const rev = Number(row.orderTotal ?? row.revenue ?? 0);
                    const prof = Number(row.profit ?? 0);
                    const marg = rev > 0 ? (prof / rev * 100) : 0;
                    return (
                      <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                        <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? row.customerName ?? '—')}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.shipType ?? '—'}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right' }}>{fmt(rev)}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: prof >= 0 ? 'var(--navy)' : 'var(--red)' }}>{fmt(prof)}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: marg >= 20 ? 'var(--orange)' : 'var(--gray-500)', fontWeight: 600 }}>{marg.toFixed(1)}%</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                          {row.shipDate ?? row.orderDate ? new Date(row.shipDate ?? row.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
